import React from 'react';
import { Alert, Button, InputNumber, Select, Space } from 'antd';

import { elementLabel } from '../items/itemValueDisplay';
import {
	MAX_GROUP_COLUMNS,
	MAX_GROUP_LINES,
	evenGroupLines,
	lostColumns,
	printableColumns,
	readableGroup,
	removeGroupColumn,
	setGroupColumns,
	setGroupLines,
	straightenGroup,
} from './pageGroupLines';

/**
 * The page designer's section for a repeating group on a form's ruled lines (KPR-1863,
 * KPE-041): how many lines, which columns, and the two ways back from fitting a real
 * form by hand. The placing itself is by dragging (PageGroupHandles). Every control is
 * a step, through the editor's `change`.
 *
 * A column the block places and this person cannot print is flagged, by its place and
 * never by its name, in words that hold for every reason it can happen: the card lost
 * it (the API will refuse the save, 400 `unknown_element`, and name it), it is hidden
 * from them, or it holds files now. The client cannot tell the first two apart, and
 * must not say that a hidden column exists.
 */
export default function PageGroupInspector({ selected, group, unit, change }) {
	const { id } = selected;
	const readable = readableGroup(group);
	const lost = readable ? lostColumns(selected, readable) : (selected.columns || []).map(column => column.name);
	const offered = printableColumns(readable).map(column => ({ value: column.name, label: elementLabel(column) || column.name }));
	const offeredNames = offered.map(option => option.value);
	const chosen = (selected.columns || []).map(column => column.name).filter(name => offeredNames.includes(name));
	const even = !Array.isArray(selected.lineTops);
	const height = Number((selected.h / selected.lines).toFixed(unit === 'mm' ? 1 : 3));

	return (
		<>
			<p className="form-panel__hint">
				Drag the area over the form&apos;s table. Drag a column&apos;s tab to move it and its right edge to size it. If the form&apos;s lines are not even, drag a line&apos;s dot. If the form sits at a slant, drag the dot at the right of the first line.
			</p>
			{readable ? null : <Alert type="warning" showIcon message="This repeating group is not one you can see on the card." description="The block prints nothing for you. Remove it, or ask someone who manages the card." />}
			<Space wrap>
				<span id={`group-lines-${id}`}>Lines</span>
				<InputNumber
					value={selected.lines}
					min={1}
					max={MAX_GROUP_LINES}
					precision={0}
					aria-labelledby={`group-lines-${id}`}
					onChange={value => value && change(latest => setGroupLines(latest, id, value), 'lines change', `group-lines:${id}`)}
				/>
			</Space>
			<p className="form-panel__hint">{even ? `Each line is ${height} ${unit} tall.` : 'The lines are spaced by hand.'}</p>
			<Space wrap>
				<Button disabled={even} onClick={() => change(latest => evenGroupLines(latest, id), 'group change')}>Space lines evenly</Button>
				<Button disabled={!selected.tilt} onClick={() => change(latest => straightenGroup(latest, id), 'group change')}>Straighten</Button>
			</Space>
			{readable ? (
				<Select
					mode="multiple"
					value={chosen}
					options={offered}
					maxCount={MAX_GROUP_COLUMNS}
					placeholder="Columns that print"
					aria-label="Columns that print"
					onChange={names => change(latest => setGroupColumns(latest, id, names, offeredNames, unit), 'group change')}
				/>
			) : null}
			{lost.length ? (
				<Alert
					type="warning"
					showIcon
					message={lost.length === 1 ? 'A column of this block cannot print for you.' : `${lost.length} columns of this block cannot print for you.`}
					description={(
						<>
							<p className="page-fit-notice__help">It is not on the card, it is not one you can see, or it holds files. A column the card has lost has to come off before the layout saves.</p>
							<Space wrap>
								{lost.map(name => (
									<Button key={name} size="small" onClick={() => change(latest => removeGroupColumn(latest, id, name), 'group change')}>
										{`Remove column ${(selected.columns || []).findIndex(column => column.name === name) + 1}`}
									</Button>
								))}
							</Space>
						</>
					)}
				/>
			) : null}
		</>
	);
}
